import React from 'react';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import Submission from '../../components/common/Submission';
import LiveTerminals, { PracticeJobHint } from '../../components/competition/LiveTerminals';
import PracticeRunHistory from '../../components/competition/PracticeRunHistory';
import { Competition } from '../../types/interfaces';

export interface SubmitTabProps {
  competition: Competition;
  isTeamMode: boolean;
  teamLoading: boolean;
  myTeam: { name?: string; lead_username?: string } | null;
  isTeamLead: boolean;
  /** Competitor or platform admin: may submit. */
  canUpload: boolean;
  /** Host who isn't a competitor: sees the submission format, can't submit. */
  previewOnly: boolean;
  practice: {
    jobHint: PracticeJobHint | null; refetchSignal: number; clearNoticeSignal: number; historyRefresh: number;
    onStarted: (hint: PracticeJobHint) => void; onConflict: () => void;
    onRunCanceled: () => void; onJobStateChange: (state: string | null, terminal: boolean) => void;
    onHasAnyRun: (has: boolean) => void;
    onSupportedChange?: (supported: boolean) => void;
  };
  logs: { show: boolean; busy: boolean; runInFlight: boolean | null; hasAnyRun: boolean | null; onDownload: () => void };
}

const PREVIEW_LINE = "Preview: hosts can see the submission format but can't submit.";

function statusLine(p: SubmitTabProps): string | null {
  const status = (p.competition.status || '').toLowerCase();
  if (status === 'new') return 'Submissions open when the competition opens.';
  if (status !== 'open') return 'This competition is closed.'; // closed, archived or anything unexpected
  if (p.previewOnly) return null; // a host has no team, so the team lines don't apply
  if (p.isTeamMode && !p.teamLoading && !p.myTeam) return "You're not on a team yet. Ask a host to add you.";
  if (p.isTeamMode && !p.teamLoading && p.myTeam && !p.isTeamLead) return p.myTeam.lead_username ? `Only your team lead (${p.myTeam.lead_username}) can submit.` : 'Only your team lead can submit.';
  return null;
}

const SubmitTab: React.FC<SubmitTabProps> = (p) => {
  const line = statusLine(p);
  const status = (p.competition.status || '').toLowerCase();
  const showPreview = p.previewOnly && status === 'open';
  const canUpload = p.canUpload && !p.previewOnly && status === 'open' && !line && !(p.isTeamMode && p.teamLoading);
  const { logs, practice } = p;
  const logsLabel = logs.busy ? 'Preparing download...'
    : logs.runInFlight === true ? 'Run in progress...'
      : logs.hasAnyRun === false ? 'No runs yet' : 'Download run logs';
  return (
    <div className="competition-submit">
      {p.previewOnly && <p className="competition-card competition-submit__status" data-tour="competition-submit-status">{PREVIEW_LINE}</p>}
      {line && <p className="competition-card competition-submit__status" data-tour="competition-submit-status">{line}</p>}
      {!p.previewOnly && p.isTeamMode && p.teamLoading && status === 'open' && <LoadingSpinner size="sm" text="Loading team information..." />}
      {showPreview && <Submission competitionId={p.competition.id} competitionType={p.competition.type} previewOnly />}
      {canUpload && (
        <Submission
          competitionId={p.competition.id}
          competitionType={p.competition.type}
          {...(p.isTeamMode ? { competitionMode: 'team', teamName: p.myTeam?.name, isTeamLead: p.isTeamLead, teamLeadUsername: p.myTeam?.lead_username } : {})}
          onPracticeStarted={practice.onStarted}
          onPracticeConflict={practice.onConflict}
          clearNoticeSignal={practice.clearNoticeSignal}
        />
      )}
      {/* Hosts previewing the format have no runs of their own, so no run controls. */}
      {logs.show && !p.previewOnly && (
        <div className="run-logs-action" data-tour="run-logs">
          <button className="btn-submit" onClick={logs.onDownload}
            disabled={logs.busy || logs.runInFlight === true || logs.hasAnyRun === false}
            title={logs.runInFlight === true ? 'Your run is still going; logs are written when it finishes'
              : logs.hasAnyRun === false ? 'No runs yet - upload a practice or graded submission first' : undefined}>
            {logsLabel}
          </button>
        </div>
      )}
      {!p.previewOnly && (<>
      <LiveTerminals competitionId={p.competition.id} practiceJobHint={practice.jobHint} refetchSignal={practice.refetchSignal}
        onRunCanceled={practice.onRunCanceled} onJobStateChange={practice.onJobStateChange}
        onSupportedChange={practice.onSupportedChange} />
      <PracticeRunHistory competitionId={p.competition.id} refreshSignal={practice.historyRefresh} onHasAnyRun={practice.onHasAnyRun} />
      </>)}
    </div>
  );
};

export default SubmitTab;
